import { getMyReferrals, getReferralSummary } from "@/api/referral";
import { GraphBox } from "@/components/molecules/GraphBox";
import { ReferrerModal } from "@/components/organisms/ReferrerModal";
import { DashboardTemplate } from "@/templates/DashboardTemplate";
import { Referral } from "@/types/global";
import {
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  Link,
  Skeleton,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useBreakpointValue,
  VStack,
} from "@chakra-ui/react";
import { faAdd, faFaceFrown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";

const STATUS_LABEL: Record<string, string> = {
  created: "Aguardando",
  confirmed: "Confirmada",
  paid: "Paga",
  expired: "Expirada",
};

const STATUS_COLOR: Record<string, string> = {
  created: "yellow",
  confirmed: "blue",
  paid: "green",
  expired: "red",
};

export function ReferralsPage() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    data: referrals = [],
    isLoading,
    refetch,
  } = useQuery<Referral[]>({
    queryKey: ["referrals"],
    queryFn: getMyReferrals,
  });

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ["referral-summary"],
    queryFn: getReferralSummary,
  });

  const isMobile = useBreakpointValue({ base: true, lg: false });

  return (
    <DashboardTemplate>
      <VStack spacing={6} width="100%" padding="20px 30px">
        <GraphBox title="Resumo de Indicações">
          <HStack
            width="100%"
            spacing={4}
            flexWrap="wrap"
            justify="space-between"
          >
            {[
              { label: "Aguardando", key: "created", color: "yellow.500" },
              { label: "Confirmadas", key: "confirmed", color: "blue.500" },
              { label: "Pagas", key: "paid", color: "green.500" },
              { label: "Expiradas", key: "expired", color: "red.500" },
            ].map(({ label, key, color }) => (
              <Box
                key={key}
                flex="1"
                minW="120px"
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                p={4}
              >
                <Skeleton isLoaded={!summaryLoading}>
                  <Stat>
                    <StatLabel color="gray.500" fontSize="sm">{label}</StatLabel>
                    <StatNumber color={color} fontSize="2xl">
                      {summary?.total_by_status?.[key as keyof typeof summary.total_by_status] ?? 0}
                    </StatNumber>
                  </Stat>
                </Skeleton>
              </Box>
            ))}
            <Box
              flex="1"
              minW="150px"
              bg="green.50"
              border="1px solid"
              borderColor="green.200"
              borderRadius="md"
              p={4}
            >
              <Skeleton isLoaded={!summaryLoading}>
                <Stat>
                  <StatLabel color="gray.600" fontSize="sm">Total Recebido</StatLabel>
                  <StatNumber color="green.600" fontSize="2xl">
                    R$ {Number(summary?.total_paid ?? 0).toFixed(2).replace(".", ",")}
                  </StatNumber>
                </Stat>
              </Skeleton>
            </Box>
            <Box
              flex="1"
              minW="150px"
              bg="blue.50"
              border="1px solid"
              borderColor="blue.200"
              borderRadius="md"
              p={4}
            >
              <Skeleton isLoaded={!summaryLoading}>
                <Stat>
                  <StatLabel color="gray.600" fontSize="sm">A Receber</StatLabel>
                  <StatNumber color="blue.600" fontSize="2xl">
                    R$ {Number(summary?.total_to_receive ?? 0).toFixed(2).replace(".", ",")}
                  </StatNumber>
                </Stat>
              </Skeleton>
            </Box>
          </HStack>
        </GraphBox>

        <GraphBox
          title="Minhas Indicações"
          padding={isMobile ? "16px" : "0px"}
          subtext="Aqui você pode verificar o status das suas indicações."
        >
          <Box width="100%" overflowX="auto">
            <Table variant="simple" size="md">
              <Thead bg="gray.50">
                <Tr>
                  <Th color="gray.700" fontWeight="bold">
                    Indicado
                  </Th>
                  <Th color="gray.700" fontWeight="bold">
                    Convênio
                  </Th>
                  <Th color="gray.700" fontWeight="bold">
                    Valor a ser pago
                  </Th>
                  <Th color="gray.700" fontWeight="bold">
                    Status
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {referrals.length == 0 && (
                  <Tr height={"150px"} position={"relative"}>
                    <VStack
                      position={"absolute"}
                      left={0}
                      right={0}
                      width={"100%"}
                      margin={"auto"}
                      paddingTop={"30px"}
                    >
                      <FontAwesomeIcon size={"2x"} icon={faFaceFrown} />
                      <Heading>Por enquanto nenhuma indicação por aqui</Heading>
                      <Text>
                        Faça indicações{" "}
                        <Link color={"blue.500"} onClick={onOpen}>
                          por aqui
                        </Link>
                      </Text>
                    </VStack>
                  </Tr>
                )}
                {referrals.map((referral) => (
                  <Tr key={referral.id} _hover={{ bg: "gray.50" }}>
                    <Td fontWeight="medium" maxW="200px">
                      <Text noOfLines={1} title={referral.name || referral.document}>
                        {referral.name || referral.document}
                      </Text>
                    </Td>
                    <Td>
                      <Badge variant="outline" textTransform="capitalize">
                        {referral.agreement?.name || "Sem convênio"}
                      </Badge>
                    </Td>
                    <Td>
                      {referral.value_paid != null
                        ? `R$ ${Number(referral.value_paid).toFixed(2).replace(".", ",")}`
                        : "-"}
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={STATUS_COLOR[referral.status]}
                        variant="subtle"
                      >
                        {STATUS_LABEL[referral.status]}
                      </Badge>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
          <ReferrerModal isOpen={isOpen} onClose={onClose} />
        </GraphBox>

        <Button
          colorScheme="green"
          position="fixed"
          bottom="50px"
          right="50px"
          borderRadius="2rem"
          height="60px"
          width={isMobile ? "60px" : "220px"}
          boxShadow="0px 5px 15px rgba(0,0,0,0.2)"
          onClick={onOpen}
          leftIcon={
            !isMobile ? (
              <FontAwesomeIcon
                style={{ paddingRight: "5px" }}
                size="lg"
                icon={faAdd}
              />
            ) : undefined
          }
          _hover={{
            transform: "scale(1.05)",
            boxShadow: "0px 8px 20px rgba(0,0,0,0.3)",
          }}
          transition="all 0.2s"
        >
          {isMobile ? (
            <FontAwesomeIcon size="lg" icon={faAdd} />
          ) : (
            "Nova Indicação"
          )}
        </Button>
      </VStack>
    </DashboardTemplate>
  );
}
